import { useState } from "react";
import { Heart, GlassWater, UtensilsCrossed, Soup, Clock, MessageCircle, PenLine, Sparkles, MapPin, ChevronDown } from "lucide-react";
import { QuoteButton } from "@/components/QuoteButton";
import { MenuButton } from "@/components/MenuButton";
import { HalalSection } from "@/components/HalalSection";
import { useContent, useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";

const Q = { service: "Traiteur uniquement", eventType: "Mariage ou cérémonie" };

const STEPS = [
  { icon: MessageCircle, title: "Vous nous racontez", text: "Votre date, votre lieu, le nombre d'invités et l'ambiance dont vous rêvez. Pas besoin de tout savoir : nous vous posons les bonnes questions." },
  { icon: PenLine, title: "Nous composons", text: "Nous vous proposons une prestation sur mesure : formule de repas, cuisine, cocktail et boissons, adaptés à vos envies et à votre projet." },
  { icon: Sparkles, title: "Vous profitez", text: "Le jour J, vous êtes avec vos proches. Nous nous occupons de la cuisine, de l'installation et du service." },
];

const FORMULES = [
  { icon: UtensilsCrossed, title: "Le buffet", text: "Convivial et généreux : vos invités se servent librement, ou les plats sont disposés directement sur les tables pour un partage chaleureux." },
  { icon: Soup, title: "Le service à l'assiette", text: "Élégant et maîtrisé : chaque invité est servi à table, pour un repas raffiné où le rythme de la soirée est parfaitement tenu." },
  { icon: Clock, title: "Le dîner en plusieurs temps", text: "Entrée, plat, dessert… le repas se déroule en plusieurs services, pour laisser place aux discours, aux animations et aux émotions." },
];

const CUISINES = [
  { title: "Cuisine africaine halal", text: "Grillades, sauces mijotées et saveurs généreuses, comme sur notre carte Safari Grill." },
  { title: "Antillaise & saveurs des îles halal", text: "Des plats colorés et parfumés, inspirés des Antilles et des îles." },
  { title: "Cuisine française halal", text: "Les classiques de la gastronomie, revisités avec élégance." },
];

function Faq({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b border-[#C9A84C]/30">
      <button onClick={() => setOpen((v) => !v)} data-testid={`mariage-faq-${i}`} className="flex w-full items-center justify-between gap-4 py-5 text-left">
        <span className="font-serif text-xl text-[#2C2C2C]">{q}</span>
        <ChevronDown size={18} className={`shrink-0 text-[#C9A84C] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-6 text-base leading-relaxed text-[#2C2C2C]/70">{a}</p>}
    </div>
  );
}

function Hero({ img }) {
  return (
    <section className="px-4 pb-16 pt-28 sm:px-6 lg:px-10 lg:pt-32" data-testid="mariage-hero">
      <div className="relative mx-auto max-w-7xl rounded-[32px] border border-[#C9A84C]/40 bg-white p-3 sm:p-4">
        <div className="relative h-[62vh] min-h-[420px] overflow-hidden rounded-[24px]">
          <img src={img} alt="Table de réception de mariage dressée au coucher du soleil" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/60 via-transparent to-transparent" />
        </div>
        <div className="relative -mt-24 mx-4 rounded-2xl bg-[#FAF6EF] p-7 shadow-[0_30px_60px_-30px_rgba(44,44,44,0.35)] sm:mx-10 lg:-mt-40 lg:ml-12 lg:mr-auto lg:max-w-2xl lg:p-12 fade-up">
          <p className="mb-4 flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-[#9A7B2C]"><Heart size={14} /> Mariages & cérémonies</p>
          <h1 className="font-serif text-4xl font-500 leading-[1.05] text-[#2C2C2C] sm:text-5xl lg:text-6xl">Le repas de votre mariage, entre de bonnes mains</h1>
          <p className="mt-4 inline-block rounded-full bg-[#C9A84C] px-4 py-1.5 text-sm font-medium text-[#2C2C2C]" data-testid="mariage-halal-badge">Traiteur mariage halal — africain, antillais, français, afro-caribéen</p>
          <p className="mt-5 text-base leading-relaxed text-[#2C2C2C]/75 lg:text-lg">
            Vous n'avez jamais organisé de réception ? C'est normal. Nous vous guidons pas à pas, du vin d'honneur au dessert, pour que vous viviez votre journée l'esprit léger.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <QuoteButton testId="mariage-hero-quote" {...Q} />
            <MenuButton variant="outline" testId="mariage-hero-carte">Découvrir notre carte</MenuButton>
          </div>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  return (
    <section className="py-20 lg:py-28" data-testid="mariage-steps">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <p className="reveal text-center text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Comment ça se passe</p>
        <h2 className="reveal mt-3 text-center font-serif text-3xl text-[#2C2C2C] lg:text-5xl">Trois étapes, aucune inquiétude</h2>
        <div className="mt-16 grid gap-10 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.title} className="reveal relative text-center" style={{ transitionDelay: `${i * 120}ms` }}>
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 font-serif text-7xl text-[#C9A84C]/20">{i + 1}</span>
              <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-[#C9A84C] shadow-[0_10px_30px_-12px_rgba(201,168,76,0.6)]"><s.icon size={24} strokeWidth={1.4} /></div>
              <h3 className="mt-6 font-serif text-2xl text-[#2C2C2C]">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2C2C2C]/70">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cocktail({ img }) {
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="mariage-cocktail">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <div className="reveal lg:col-span-5">
          <p className="text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Le premier moment de la fête</p>
          <h2 className="mt-3 font-serif text-3xl text-[#2C2C2C] lg:text-5xl">Cocktail de bienvenue & vin d'honneur</h2>
          <p className="mt-6 text-base leading-relaxed text-[#2C2C2C]/70">
            Après la cérémonie, vos invités se retrouvent autour de bouchées salées et sucrées, de petits fours et de boissons fraîches. C'est le moment des retrouvailles et des photos : nous le rendons gourmand et élégant.
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#2C2C2C]/70">
            Le cocktail peut se suffire à lui-même, ou précéder le repas : vous choisissez ce qui convient à votre journée.
          </p>
          <div className="mt-8"><MenuButton variant="primary" testId="mariage-cocktail-carte">Découvrir notre carte</MenuButton></div>
        </div>
        <div className="reveal relative lg:col-span-7">
          <div className="overflow-hidden rounded-t-[200px] rounded-b-2xl">
            <img src={img} alt="Bouchées raffinées et cocktails sans alcool pour un vin d'honneur" className="aspect-[4/3] w-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Formules() {
  return (
    <section className="py-20 lg:py-28" data-testid="mariage-formules">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="reveal mx-auto max-w-2xl text-center">
          <p className="text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Le repas</p>
          <h2 className="mt-3 font-serif text-3xl text-[#2C2C2C] lg:text-5xl">Choisissez la formule qui vous ressemble</h2>
          <p className="mt-5 text-base text-[#2C2C2C]/70">Vous hésitez ? C'est tout naturel. Nous vous aidons à choisir selon votre lieu, votre nombre d'invités et le déroulé de votre soirée.</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {FORMULES.map((f, i) => (
            <div key={f.title} data-testid={`mariage-formule-${i}`} className="reveal group flex min-h-[340px] flex-col justify-between rounded-[28px] border border-[#C9A84C]/40 bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-30px_rgba(44,44,44,0.4)]" style={{ transitionDelay: `${i * 100}ms` }}>
              <f.icon size={30} strokeWidth={1.2} className="text-[#C9A84C]" />
              <div>
                <p className="font-serif text-sm italic text-[#9A7B2C]">Formule {String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-serif text-3xl text-[#2C2C2C]">{f.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#2C2C2C]/70">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SansAlcool() {
  return (
    <section className="px-5 lg:px-8" data-testid="mariage-sans-alcool">
      <div className="reveal mx-auto flex max-w-6xl flex-col items-center gap-8 rounded-[28px] bg-[#C9A84C] px-8 py-14 text-center md:flex-row md:text-left lg:px-16">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-white/25 text-[#2C2C2C]"><GlassWater size={34} strokeWidth={1.3} /></div>
        <div>
          <h2 className="font-serif text-3xl text-[#2C2C2C] lg:text-4xl">Des cocktails sans alcool pour tous vos invités</h2>
          <p className="mt-3 max-w-2xl text-base text-[#2C2C2C]/80">Frais, colorés et pensés pour trinquer ensemble : nos cocktails sans alcool accompagnent votre vin d'honneur comme votre soirée, pour que chacun lève son verre.</p>
        </div>
      </div>
    </section>
  );
}

function Cuisines({ img }) {
  return (
    <section className="py-20 lg:py-28" data-testid="mariage-cuisines">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="reveal overflow-hidden rounded-[28px]">
          <img src={img} alt="Buffet de mariage mêlant cuisine africaine, créole et française" className="h-72 w-full object-cover lg:h-[420px]" />
        </div>
        <div className="relative z-10 mx-auto -mt-16 grid max-w-6xl gap-5 px-2 md:grid-cols-3 lg:-mt-24">
          {CUISINES.map((c, i) => (
            <div key={c.title} data-testid={`mariage-cuisine-${i}`} className="reveal rounded-t-[120px] rounded-b-2xl border border-[#C9A84C]/40 bg-[#FAF6EF] px-7 pb-9 pt-14 text-center" style={{ transitionDelay: `${i * 100}ms` }}>
              <h3 className="font-serif text-2xl text-[#2C2C2C]">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2C2C2C]/70">{c.text}</p>
            </div>
          ))}
        </div>
        <p className="reveal mx-auto mt-10 max-w-2xl text-center text-base text-[#2C2C2C]/70">
          Vous pouvez mélanger les saveurs : toutes nos cuisines sont proposées en <strong>halal</strong>, notre offre principale, et une option non halal reste possible sur demande. Vous avez déjà une idée de menu ? Nous la réalisons pour vous.
        </p>
        <div className="reveal mt-8 flex flex-wrap justify-center gap-4">
          <MenuButton variant="primary" testId="mariage-cuisines-carte">Découvrir notre carte</MenuButton>
          <QuoteButton variant="outline" testId="mariage-cuisines-quote" {...Q} />
        </div>
      </div>
    </section>
  );
}

export default function Mariages() {
  useReveal();
  const { content } = useContent();
  const img = useSiteImage();
  const faqs = [
    { q: "Je ne sais pas encore quelle formule choisir, est-ce un problème ?", a: "Pas du tout. Dans le formulaire de devis, vous pouvez indiquer « Je souhaite être conseillée » : nous vous orientons vers la formule la plus adaptée à votre journée." },
    { q: "Peut-on composer un menu sur mesure ?", a: "Oui. Nos cartes sont une base d'inspiration : nous adaptons les plats à vos envies, à vos traditions familiales et à vos invités." },
    { q: "Proposez-vous des plats halal ?", a: "Oui, c'est même notre offre principale : traiteur africain halal, antillais halal, français halal et afro-caribéen halal. Une option non halal est également disponible sur demande." },
    { q: "Pouvez-vous aussi coordonner le jour J ?", a: "Oui. Vous pouvez associer le traiteur à notre service de coordination événementielle, pour avoir un seul interlocuteur le jour de votre mariage." },
    { q: "Où intervenez-vous ?", a: content?.zones?.traiteur || "Dans l'Oise, en Île-de-France et dans les Hauts-de-France." },
  ];

  return (
    <div data-testid="mariages-page" className="bg-[#FAF6EF]">
      <Seo
        title="Traiteur mariage halal africain, antillais, français & afro-caribéen — Afro Regal"
        description="Traiteur de mariage halal : traiteur africain halal, antillais halal, français halal et afro-caribéen halal. Cocktail de bienvenue, vin d'honneur, buffet, service à l'assiette ou dîner en plusieurs temps, cocktails sans alcool. Option non halal sur demande. Devis personnalisé."
      />
      <Hero img={img("mariage_hero")} />
      <Steps />
      <Cocktail img={img("mariage_cocktail")} />
      <Formules />
      <SansAlcool />
      <Cuisines img={img("mariage_cuisine")} />
      <HalalSection variant="light" title="Un mariage halal, sans compromis sur l'élégance" intro="Pour que tous vos invités partagent le même repas en toute sérénité, notre cuisine de mariage est pensée en halal : africaine, antillaise, française ou afro-caribéenne." testId="mariage-halal" />

      <section className="bg-white py-20 lg:py-28" data-testid="mariage-faq">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="reveal lg:col-span-4">
            <p className="text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Vos questions</p>
            <h2 className="mt-3 font-serif text-3xl text-[#2C2C2C] lg:text-4xl">Tout ce que vous vous demandez</h2>
            <p className="mt-4 flex items-start gap-2 text-sm text-[#2C2C2C]/60"><MapPin size={16} className="mt-0.5 shrink-0 text-[#C9A84C]" /> Toutes nos prestations sont sur devis, étudiées pour votre mariage.</p>
          </div>
          <div className="reveal lg:col-span-8">
            {faqs.map((f, i) => <Faq key={f.q} {...f} i={i} />)}
          </div>
        </div>
      </section>

      <section className="py-24 text-center" data-testid="mariage-final-cta">
        <div className="mx-auto max-w-2xl px-5">
          <Heart size={26} strokeWidth={1.3} className="mx-auto text-[#C9A84C]" />
          <h2 className="mt-5 font-serif text-3xl text-[#2C2C2C] lg:text-5xl">Parlez-nous de votre mariage</h2>
          <p className="mt-4 text-base text-[#2C2C2C]/70">Quelques informations suffisent pour commencer. Nous revenons vers vous avec une proposition pensée pour vous.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <QuoteButton testId="mariage-final-quote" {...Q} />
            <MenuButton variant="outline" testId="mariage-final-carte">Découvrir notre carte</MenuButton>
          </div>
        </div>
      </section>

      <div className="fixed bottom-6 left-6 z-40 hidden lg:block" data-testid="mariage-sticky-quote">
        <QuoteButton testId="mariage-sticky-quote-btn" variant="gold" className="shadow-[0_15px_35px_-12px_rgba(44,44,44,0.5)]" {...Q}>
          <Heart size={15} /> Demander un devis mariage
        </QuoteButton>
      </div>
    </div>
  );
}
